import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import "../css/AminiiDashboard.css";

const POLICIES_DATA = {
  "shipping-policy": {
    title: "Shipping & Delivery Policy",
    subtitle: "We will deliver to you within 2 to 3 business days across India.",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Fast Delivery Timeline",
        content: `We process and dispatch all confirmed orders promptly. We will deliver to you within 2 to 3 business days across India. Transit times are estimates and may vary slightly based on courier routes or remote pin codes.`,
      },
      {
        heading: "2. Order Processing & Dispatch",
        content: `Orders are packed and dispatched within 24 to 48 hours of order confirmation. Customers must provide a complete and accurate delivery address along with a reachable contact number.`,
      },
      {
        heading: "3. Undeliverable Parcels & Address Accuracy",
        content: `If a parcel cannot be delivered because of an incorrect address, repeated delivery failure, or customer unavailability, additional shipping charges may apply for re-dispatch where permitted.`,
      },
      {
        heading: "4. Inspection upon Delivery",
        content: `Customers should inspect the package upon delivery where possible. If there is visible external damage or tampering, please photograph the package and contact AMINII support immediately at 9606132902.`,
      },
      {
        heading: "5. Tracking & Courier Support",
        content: `Once an order is handed to our logistics partner (Shiprocket / Express Courier), live tracking information is provided on your account page and sent via SMS/Email. While transit delays are outside AMINII's direct control, our support team will actively assist with tracking and courier follow-up.`,
      },
    ],
  },
  "return-policy": {
    title: "Exchange Policy",
    subtitle: "48-Hour Exchange Window — Important Details",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. 48-Hour Exchange Notification Window",
        content: `We want you to be delighted with your AMINII purchase. Exchange requests must be notified to AMINII within 48 HOURS (2 DAYS) of delivery by contacting us on WhatsApp at 9606132902 or email at aminiiclothings@gmail.com.`,
      },
      {
        heading: "2. No Return & Refund Policy",
        content: `AMINII follows a strict NO-RETURN policy. We do NOT accept returns for a refund or change-of-mind. We offer size or style exchange ONLY when the order meets our eligibility criteria.`,
      },
      {
        heading: "3. Eligibility Criteria for Exchange",
        content: `An exchange may be considered if:\n• You contact us within 48 hours of delivery.\n• The product is unused, unworn, unwashed, and unaltered.\n• Original brand tags, luxury packaging, and accompanying materials are fully intact.\n• The product is completely free from perfume, makeup stains, damage, or signs of use.\n• Order number and clear photos/video are provided upon request.\n• The requested replacement size/item is available in stock.`,
      },
      {
        heading: "4. Damaged or Defective Items",
        content: `If you receive a damaged, defective, or incorrect product, contact us within 48 hours of delivery with unboxing photos/video of the package and item. We will review the issue and arrange an appropriate exchange resolution.`,
      },
      {
        heading: "5. Non-Exchangeable Items",
        content: `Products used, washed, ironed, altered, damaged after delivery, items missing original tags, or requests made after the 48-hour notification window are strictly ineligible for exchange.`,
      },
      {
        heading: "6. Exchange Shipping Charges",
        content: `For customer-requested size or style exchanges, reverse pickup or shipping charges (where applicable) are payable by the customer. For verified wrong-item or manufacturing defect claims, AMINII will cover the shipping arrangements.`,
      },
    ],
  },
  "cancellation-policy": {
    title: "Cancellation Policy",
    subtitle: "Order Cancellation Rules & Timelines",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Pre-Dispatch Cancellation",
        content: `Orders may be cancelled before dispatch by contacting AMINII support as soon as possible via WhatsApp (9606132902) or email (aminiiclothings@gmail.com).`,
      },
      {
        heading: "2. Post-Dispatch Limitation",
        content: `Once an order has been dispatched and handed over to the courier partner, cancellation is no longer possible.`,
      },
      {
        heading: "3. Exceptional Cancellations by AMINII",
        content: `AMINII reserves the right to cancel an order in exceptional situations such as stock unavailability, pricing errors, suspected fraudulent transactions, or delivery area restrictions. In such cases, any eligible amount paid will be fully refunded according to law.`,
      },
    ],
  },
  "refund-policy": {
    title: "Refund Policy",
    subtitle: "Guidelines on Payment Refunds",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. General Refund Rule",
        content: `AMINII operates under a No-Return policy. We do not provide cash/monetary refunds for change of mind, color preference, or incorrect size selection where the product itself is free of defects.`,
      },
      {
        heading: "2. Approved Refunds",
        content: `Where a refund is legally required or specifically approved by AMINII (e.g. an accepted defect claim where a suitable replacement size is out of stock), the refund will be processed using the original payment method.`,
      },
      {
        heading: "3. Refund Processing Timelines",
        content: `Approved refunds are initiated within 48 hours and typically credit back to your original payment account (bank account / UPI / card) within 5 to 7 business days depending on payment gateway and banking timelines.`,
      },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    subtitle: "How we collect, use, and safeguard your personal information.",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Information We Collect",
        content: `We collect personal information such as your name, phone number, email address, shipping/billing address, order details, and customer support communications when you browse, register, or place an order with AMINII.`,
      },
      {
        heading: "2. How We Use Information",
        content: `We use your data to process and deliver orders, provide customer support, communicate order tracking updates, prevent fraud, improve our catalog, and send promotional offers (where opted-in).`,
      },
      {
        heading: "3. Payment Security",
        content: `All online payments are securely processed through PCI-DSS compliant third-party payment service providers (Razorpay). AMINII does not store complete credit/debit card details or confidential banking credentials on its servers.`,
      },
      {
        heading: "4. Service Providers & Third Parties",
        content: `We share necessary shipping details with trusted logistics partners (Shiprocket/courier partners) to fulfill delivery. We never sell, rent, or trade your personal data to external marketing companies.`,
      },
      {
        heading: "5. Privacy Enquiries",
        content: `For privacy-related questions or data requests, contact us via email at aminiiclothings@gmail.com or WhatsApp at 9606132902.`,
      },
    ],
  },
  "terms-of-service": {
    title: "Terms & Conditions",
    subtitle: "Terms and conditions governing your use of AMINII Official Store.",
    lastUpdated: "August 2026",
    sections: [
      {
        heading: "1. Website Use & Agreement",
        content: `Welcome to AMINII CLOTHING. By accessing or using our website, placing an order, or purchasing our products, you agree to be bound by these Terms & Conditions.`,
      },
      {
        heading: "2. Product Information & Handcrafted Variations",
        content: `We make every reasonable effort to display product descriptions, colors, measurements, embroidery, and fabric details accurately. Actual colors may vary slightly due to studio lighting and screen settings. Minor variations in embroidery, print placement, or texture may occur as garments involve handcrafted production processes.`,
      },
      {
        heading: "3. Prices & Order Acceptance",
        content: `All prices are listed in Indian Rupees (INR) inclusive of applicable taxes. An order is accepted only after successful payment processing or COD verification, subject to stock availability. AMINII reserves the right to revise prices or cancel orders affected by pricing/inventory errors.`,
      },
      {
        heading: "4. Intellectual Property",
        content: `All content, brand name, logo, photographs, graphics, videos, and product designs belong exclusively to AMINII CLOTHING. Unauthorized copying, reproduction, or commercial use without written permission is strictly prohibited.`,
      },
      {
        heading: "5. Governing Law & Jurisdiction",
        content: `These terms shall be governed by the laws of India. Any dispute arising under these terms shall be subject to the jurisdiction of competent courts in Shivamogga, Karnataka.`,
      },
    ],
  },
};

function AminiiPolicies() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname.replace("/", "");
  const activeKey = POLICIES_DATA[currentPath] ? currentPath : "shipping-policy";
  const policy = POLICIES_DATA[activeKey];

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirstName, setUserFirstName] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const user = sessionStorage.getItem("user");
    if (user) {
      try {
        const parsed = JSON.parse(user);
        setIsLoggedIn(true);
        setUserFirstName(parsed.firstName || parsed.name?.split(" ")[0] || "User");
      } catch (e) {}
    }
  }, [location.pathname]);

  return (
    <div className="amin-dashboard">
      <AminiiNavbar
        isLoggedIn={isLoggedIn}
        userFirstName={userFirstName}
        cartItemCount={0}
      />

      <div style={{ maxWidth: "1100px", margin: "120px auto 60px auto", padding: "0 20px" }}>
        {/* Navigation Tabs */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            borderBottom: "2px solid #eaeaea",
            marginBottom: "40px",
            overflowX: "auto",
            paddingBottom: "10px",
          }}
        >
          {Object.keys(POLICIES_DATA).map((key) => {
            const isActive = key === activeKey;
            return (
              <button
                key={key}
                onClick={() => navigate(`/${key}`)}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderBottom: isActive ? "3px solid #1a1a1a" : "3px solid transparent",
                  background: "none",
                  fontWeight: isActive ? "700" : "500",
                  color: isActive ? "#1a1a1a" : "#666",
                  fontSize: "14px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  fontFamily: "var(--font-ui)",
                  transition: "all 0.2s ease",
                }}
              >
                {POLICIES_DATA[key].title}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div style={{ marginBottom: "40px", textAlign: "left" }}>
          <h1
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "36px",
              fontWeight: "700",
              color: "#1a1a1a",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            {policy.title}
          </h1>
          <p style={{ color: "#666", fontSize: "16px" }}>{policy.subtitle}</p>
          <span style={{ fontSize: "12px", color: "#999", display: "block", marginTop: "6px" }}>
            Last Updated: {policy.lastUpdated}
          </span>
        </div>

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
          {policy.sections.map((sec, idx) => (
            <div
              key={idx}
              style={{
                backgroundColor: "#fcfcfc",
                border: "1px solid #eeeeee",
                borderRadius: "8px",
                padding: "24px 28px",
              }}
            >
              <h3
                style={{
                  fontFamily: "var(--font-heading)",
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                  marginBottom: "10px",
                }}
              >
                {sec.heading}
              </h3>
              <p style={{ color: "#444", fontSize: "15px", lineHeight: "1.7", margin: 0 }}>
                {sec.content}
              </p>
            </div>
          ))}
        </div>
      </div>

      <AminiiFooter />
    </div>
  );
}

export default AminiiPolicies;
